// ------------------------------------------------------------------------------------------------
import * as React from 'react';
import _ from 'lodash';

// material-ui components
import Paper from 'material-ui/Paper';
import IconButton from 'material-ui/IconButton';
import AutoComplete from 'material-ui/AutoComplete';
import SearchIcon from 'material-ui/svg-icons/action/search';
import CloseIcon from 'material-ui/svg-icons/navigation/close';
import { grey500 } from 'material-ui/styles/colors';

// Internal functions
import { isDefined } from '@Utils/Micro';
import { isAnimationEnabled } from '@Utils/Globals';
// ------------------------------------------------------------------------------------------------
const getStyles = (props: MUISearchBarProps, state: MUISearchBarState) => {
    const { disabled, iconButtonStyle, inactiveTextColor, activeTextColor } = props;
    const { value } = state;
    const nonEmpty = value.length > 0
    const textColor = nonEmpty ? activeTextColor : inactiveTextColor;
    const commonButtonStyle: React.CSSProperties = {
        marginLeft: -20,
        marginRight: -10,
        height: 'inherit',
        lineHeight: 'inherit',
        opacity: !disabled ? 0.54 : 0.38,
        transition: 'transform 200ms cubic-bezier(0.4, 0.0, 0.2, 1)'
    };
    return {
        root: {
            height: 48,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            lineHeight: '48px'
        } as React.CSSProperties,
        iconButtonClose: {
            style: {
                display: nonEmpty ? 'inline-block' : 'none',
                // transform: nonEmpty ? 'scale(1, 1)' : 'scale(0, 0)',
                ...commonButtonStyle,
                ...iconButtonStyle
            } as React.CSSProperties,
            iconStyle: {
                opacity: nonEmpty ? 1 : 0,
                transition: 'opacity 200ms cubic-bezier(0.4, 0.0, 0.2, 1)'
            } as React.CSSProperties
        },
        iconButtonSearch: {
            style: {
                display: !nonEmpty ? 'inline-block' : 'none',
                // transform: nonEmpty ? 'scale(0, 0)' : 'scale(1, 1)',
                ...commonButtonStyle,
                ...iconButtonStyle
            } as React.CSSProperties,
            iconStyle: {
                opacity: nonEmpty ? 0 : 1,
                transition: 'opacity 200ms cubic-bezier(0.4, 0.0, 0.2, 1)'
            } as React.CSSProperties
        },
        hintStyle: {
            bottom: 0,
            color: textColor
        },
        input: {
            width: '100%',
            height: 'inherit',
            lineHeight: 'inherit',
            color: textColor
        } as React.CSSProperties,
        searchContainer: {
            margin: 'auto 16px',
            width: '100%',
            height: 'inherit'
        } as React.CSSProperties
    }
}
// ------------------------------------------------------------------------------------------------
/**
 * Search bar component's properties.
 *
 * @export
 * @interface MUISearchBarProps
 */
export interface MUISearchBarProps {

    /**
     * Override the close icon.
     *
     * @type {Node}
     * @memberof MUISearchBarProps
     */
    closeIcon?: React.ReactNode;

    /**
     * Array of strings or nodes used to populate the list.
     *
     * @type {(string[] | Node[])}
     * @memberof MUISearchBarProps
     */
    dataSource: string[] | React.ReactNode[];

    /**
     * Config for objects list dataSource.
     *
     * @type {object}
     * @memberof MUISearchBarProps
     */
    dataSourceConfig?: { text: string; value: string; };

    /**
     * Disables text field.
     *
     * @type {boolean}
     * @memberof MUISearchBarProps
     */
    disabled?: boolean,

    /**
     * Sets hint text for the embedded text field.
     *
     * @type {string}
     * @memberof MUISearchBarProps
     */
    hintText?: string;

    /**
     * Fired when the text value changes.
     *
     * @type {Function}
     * @memberof MUISearchBarProps
     */
    onChange?: (newValue: string) => void;

    /**
     * Fired when the search icon is clicked.
     *
     * @type {Function}
     * @memberof MUISearchBarProps
     */
    onRequestSearch?: React.MouseEventHandler<any>;

    /**
     * Override the search icon.
     *
     * @type {Node}
     * @memberof MUISearchBarProps
     */
    searchIcon?: React.ReactNode;

    /**
     * Override the search icon.
     *
     * @type {Node}
     * @memberof MUISearchBarProps
     */
    extraMenuItems?: React.ReactNode;

    /**
     * Specifies whether the element to have its spelling and grammar checked or not.
     *
     * @type {boolean}
     * @memberof MUISearchBarProps
     */
    spellCheck?: boolean,

    /**
     * Override the inline-styles of the button element.
     *
     * @type {React.CSSProperties}
     * @memberof MUISearchBarProps
     */
    iconButtonStyle?: React.CSSProperties;

    /**
     * Override the inline-styles of the root element.
     *
     * @type {React.CSSProperties}
     * @memberof MUISearchBarProps
     */
    style?: React.CSSProperties;

    /**
     * The value of the text field.
     *
     * @type {*}
     * @memberof MUISearchBarProps
     */
    value?: string;

    /**
     * The value of the text field.
     *
     * @type {*}
     * @memberof MUISearchBarProps
     */
    filter?(searchText: string, key: string, item: any): boolean;

    /**
     * Text color used text input when in focus.
     *
     * @type {string}
     * @memberof MUISearchBarProps
     */
    activeTextColor?: string;

    /**
     * Text color used text input when NOT in focus.
     * @type {string}
     * @memberof MUISearchBarProps
     */
    inactiveTextColor?: string;
}
// ------------------------------------------------------------------------------------------------
/**
 * Search bar component's state.
 *
 * @export
 * @interface SearchBarState
 */
export interface MUISearchBarState {
    focus: boolean;
    value: string;
    active: boolean;
}
// ------------------------------------------------------------------------------------------------
/**
 * Material design search bar
 * @see [Search patterns](https://material.io/guidelines/patterns/search.html)
 */
export class MUISearchBar extends React.Component<MUISearchBarProps, MUISearchBarState> {
    public static defaultProps: MUISearchBarProps = {
        closeIcon: <CloseIcon color={grey500} />,
        dataSource: [],
        dataSourceConfig: { text: 'text', value: 'value' },
        disabled: false,
        hintText: 'Search',
        searchIcon: <SearchIcon color={grey500} />,
        spellCheck: false,
        value: ''
    };
    private autoComplete: any = null;
    // ------------------------------------------------------------------------------------------------
    constructor(props) {
        super(props)
        this.state = {
            focus: false,
            value: this.props.value,
            active: false
        }
    }
    // ------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps) {
        if (this.props.value !== prevProps.value) {
            this.setState({ value: this.props.value })
        }
    }
    // ------------------------------------------------------------------------------------------------
    /**
     * Focus the search field.
     * @public
     */
    focus() {
        this.autoComplete.focus()
    }
    // ------------------------------------------------------------------------------------------------
    /**
     * Blurs the search field.
     * @public
     */
    blur() {
        this.autoComplete.blur()
    }
    // ------------------------------------------------------------------------------------------------
    handleFocus() {
        this.setState({ focus: true })
    }
    // ------------------------------------------------------------------------------------------------
    handleBlur() {
        this.setState({ focus: false })
        if (this.state.value.trim().length === 0) {
            this.setState({ value: '' })
        }
    }
    // ------------------------------------------------------------------------------------------------
    handleInput(e) {
        this.setState({ value: e })
        this.props.onChange(e)
    }
    // ------------------------------------------------------------------------------------------------
    handleCancel() {
        this.setState({ active: false, value: '' })
        this.props.onChange('')
    }
    // ------------------------------------------------------------------------------------------------
    handleKeyPressed(e) {
        if (e.charCode === 13) {
            this.props.onRequestSearch(e)
        }
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        const styles = getStyles(this.props, this.state);
        const { value } = this.state;
        const inputProps = _.omit(this.props, [
            "onChange",
            "closeIcon",
            "disabled",
            "onRequestSearch",
            "searchIcon",
            "extraMenuItems",
            "spellCheck",
            "style",
            "iconButtonStyle",
            "activeTextColor",
            "inactiveTextColor"
        ]);
        const {
            closeIcon,
            disabled,
            onRequestSearch,
            searchIcon,
            extraMenuItems,
            style
        } = this.props;

        if (isDefined(style.height))
            styles.root.lineHeight = style.height + 'px';

        return (
            <Paper
                transitionEnabled={false}
                style={{
                    ...styles.root,
                    ...style
                }}
            >
                <div style={styles.searchContainer}>
                    <AutoComplete
                        ref={(ref: any) => { this.autoComplete = ref }}
                        onBlur={() => this.handleBlur()}
                        searchText={value}
                        onUpdateInput={(e) => this.handleInput(e)}
                        onKeyPress={(e) => this.handleKeyPressed(e)}
                        onFocus={() => this.handleFocus()}
                        fullWidth
                        hintStyle={styles.hintStyle}
                        style={styles.input}
                        inputStyle={styles.input}
                        textFieldStyle={styles.input}
                        underlineShow={false}
                        disabled={disabled}
                        disableFocusRipple={!isAnimationEnabled()}
                        {...inputProps}
                    />
                </div>
                {isDefined(closeIcon) ?
                    <IconButton
                        onClick={() => this.handleCancel()}
                        iconStyle={styles.iconButtonClose.iconStyle}
                        style={styles.iconButtonClose.style}
                        disabled={disabled}>
                        {closeIcon}
                    </IconButton>
                    : null}
                {isDefined(searchIcon) ?
                    <IconButton
                        onClick={onRequestSearch}
                        iconStyle={styles.iconButtonSearch.iconStyle}
                        style={styles.iconButtonSearch.style}
                        disabled={disabled}>
                        {searchIcon}
                    </IconButton>
                    : null}
                {extraMenuItems}
            </Paper>
        )
    }
}

